import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PackageSearch, X } from "lucide-react";
import { api, errorMessage, Product, Variant } from "../lib/api";
import { colourOf } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

interface PickCtx { userId: string | null; memberName: string; bottles: number; add: (p: Product, v: Variant, qty?: number) => Promise<void>; stop: () => void }
const Ctx = createContext<PickCtx>({ userId: null, memberName: "", bottles: 0, add: async () => {}, stop: () => {} });
export const usePick = () => useContext(Ctx);
const KEY = "hourlier.pickFor";

export function PickProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = !!user && typeof user === "object" && user.role === "admin";
  const [userId, setUserId] = useState<string | null>(() => sessionStorage.getItem(KEY));
  const [slot, setSlot] = useState<string>(() => sessionStorage.getItem(KEY + ":slot") || "a");
  const [bottles, setBottles] = useState(0);
  useEffect(() => { const p = params.get("pick"); if (p && isAdmin) { sessionStorage.setItem(KEY, p); setUserId(p); const sl = params.get("slot") || "a"; sessionStorage.setItem(KEY + ":slot", sl); setSlot(sl); } }, [params, isAdmin]);
  const active = isAdmin && !!userId;
  const { data: member } = useQuery<{ user: { name: string } }>({ queryKey: ["admin-customer", userId], queryFn: () => api.get(`/admin/customers/${userId}`).then((r) => r.data), enabled: active });
  const { data: draft } = useQuery<{ lines: { quantity: number }[] }>({ queryKey: ["case-draft", userId, slot], queryFn: () => api.get(`/admin/customers/${userId}/case-draft?slot=${slot}`).then((r) => r.data), enabled: active });
  useEffect(() => { if (draft) setBottles(draft.lines.reduce((s, l) => s + l.quantity, 0)); }, [draft]);
  const value = useMemo<PickCtx>(() => ({
    userId: active ? userId : null, memberName: member?.user.name || "member", bottles,
    add: async (p, v, qty = 1) => {
      try {
        const { data } = await api.post(`/admin/customers/${userId}/case-draft/add?slot=${slot}`, { product_handle: p.handle, variant_id: v.id, title: p.title, variant_title: v.title, unit_price_pence: v.price_pence, quantity: qty, image: p.image, colour: colourOf(p.product_type, ...(p.tags || []), p.title) });
        setBottles(data.bottles);
        toast.success(`${p.title} added to ${data.member_name || "the member"}'s case · ${data.bottles} bottle${data.bottles === 1 ? "" : "s"}`);
      } catch (e) { toast.error(errorMessage(e)); }
    },
    stop: () => { sessionStorage.removeItem(KEY); setUserId(null); },
  }), [active, userId, member, bottles]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function PickBanner() {
  const { userId, memberName, bottles, stop } = usePick();
  if (!userId) return null;
  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 bg-[var(--wine)] px-6 py-2 text-sm text-[var(--paper)]" data-testid="pick-banner">
      <span className="flex items-center gap-2"><PackageSearch className="h-4 w-4" strokeWidth={1.5} /><strong>Staff pick mode</strong> — choosing wines for <strong data-testid="pick-member">{memberName}</strong>'s case. <span data-testid="pick-count">{bottles} bottle{bottles === 1 ? "" : "s"}</span> so far; they appear in the case builder as you add them.</span>
      <span className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-none border-[var(--paper)]/60 bg-transparent text-[var(--paper)] hover:bg-[var(--paper)] hover:text-[var(--wine)]" onClick={() => { if (window.opener) window.close(); else stop(); }} data-testid="pick-done">Done — back to console</Button>
        <button onClick={stop} className="p-1 opacity-70 hover:opacity-100" title="Leave pick mode" data-testid="pick-exit"><X className="h-4 w-4" /></button>
      </span>
    </div>
  );
}
